import { FIELD_SURFACE } from "@/components/ui/field-surface";
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const inputVariants = cva(
  "flex w-full border shadow-none transition-colors outline-none focus:outline-none focus-visible:outline-none file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-chilli-red-light aria-invalid:bg-chilli-blush aria-invalid:focus:border-chilli-red-light",
  {
    variants: {
      variant: {
        default:
          "h-12 border-input bg-transparent px-3 py-1 text-base placeholder:text-muted-foreground focus:border-brand-yellow focus:ring-0 focus-visible:border-brand-yellow focus-visible:ring-0 md:text-sm",
        // The canonical modular-form input, replacing the classname string
        // that was pasted across create/edit forms.
        form: cn(FIELD_SURFACE, "h-auto px-6 py-4 font-inter text-sm leading-normal text-off-black placeholder:font-normal placeholder:text-grey-light disabled:shadow-none"),
      },
    },
    defaultVariants: { variant: "default" },
  }
)

interface InputProps
  extends React.ComponentProps<"input">,
    VariantProps<typeof inputVariants> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, variant, onWheel, onKeyDown, ...props }, ref) => {
    // A wheel tick or an arrow key over a focused number field silently steps
    // its value — one tick moves a money field by a cent with nothing on screen
    // to show it (GRAIN-2970). The spinners are already hidden app-wide
    // (entrypoints/application.css), so stepping is an invisible affordance.
    // Guarding here means a number field added later cannot miss the fix.
    const isNumber = type === "number"

    // Blur rather than preventDefault: cancelling the wheel event would also
    // cancel the page scroll (and React's root wheel listener is passive, so
    // preventDefault here is a no-op anyway). An unfocused field does not step.
    const handleWheel = (event: React.WheelEvent<HTMLInputElement>) => {
      onWheel?.(event)
      event.currentTarget.blur()
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event)
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault()
      }
    }

    return (
      <input
        type={type}
        className={cn(inputVariants({ variant }), className)}
        ref={ref}
        onWheel={isNumber ? handleWheel : onWheel}
        onKeyDown={isNumber ? handleKeyDown : onKeyDown}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input, inputVariants }
